import {
    Control,
    FieldArrayWithId,
    FieldError,
    FieldErrorsImpl,
    Merge,
    useWatch,
} from 'react-hook-form';
import {
    IJobQuestionsForm,
    IQuestionOption,
    JobQuestionObjectFields,
} from '../../../../../../common';
import {
    Icon,
    PargodaRoundButton,
    Typography,
    useTheme,
} from '../../../../../../base';
import {
    StyledOptionsContainer,
    StyledOptionsListWrapper,
    StyledAddOptionButtonContainer,
} from '../../JobQuestions.styles';
import { useTranslation } from 'react-i18next';
import { OptionItem } from './OptionItem';

interface QuestionOptionsProps {
    fieldNamePrefix: `questions.${number}`;
    control: Control<IJobQuestionsForm>;
    optionFields?: FieldArrayWithId<
        IJobQuestionsForm,
        `questions.${number}.options`,
        'id'
    >[];
    isDealbreaker?: boolean;
    hasBeenSubmitted?: boolean;
    optionsError?:
        | Merge<
              FieldError,
              (
                  | Merge<
                        FieldError,
                        FieldErrorsImpl<{
                            id: string;
                            value: string;
                            isCorrectAnswer?: boolean;
                        }>
                    >
                  | undefined
              )[]
          >
        | undefined;
    noOptionsErrorMessage?: string | null;
    questionType?: string;
    onAddOption?: () => void;
    onRemoveOption?: (optionIndex: number) => void;
    onDealbreakerAnswerChange?: (optionIndex: number) => void;
}

export const QuestionOptions = ({
    fieldNamePrefix,
    control,
    optionFields,
    isDealbreaker,
    hasBeenSubmitted,
    optionsError,
    noOptionsErrorMessage,
    questionType,
    onAddOption,
    onRemoveOption,
    onDealbreakerAnswerChange,
}: QuestionOptionsProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const watchedOptions = useWatch({
        control,
        name: `${fieldNamePrefix}.${JobQuestionObjectFields.OPTIONS}` as `questions.${number}.options`,
    }) as Array<IQuestionOption> | undefined;

    return (
        <StyledOptionsContainer>
            <StyledOptionsListWrapper>
                {optionFields?.map(
                    (
                        field: FieldArrayWithId<
                            IJobQuestionsForm,
                            `questions.${number}.options`,
                            'id'
                        >,
                        optionIndex: number
                    ) => {
                        const optionValueError =
                            hasBeenSubmitted &&
                            Array.isArray(optionsError) &&
                            optionsError[optionIndex]?.label;

                        const isCorrectAnswer =
                            watchedOptions?.[optionIndex]?.isCorrectAnswer ||
                            false;

                        return (
                            <OptionItem
                                key={field.id}
                                fieldNamePrefix={fieldNamePrefix}
                                control={control}
                                optionIndex={optionIndex}
                                isCorrectAnswer={isCorrectAnswer}
                                isDealbreaker={isDealbreaker || false}
                                hasBeenSubmitted={hasBeenSubmitted || false}
                                optionValueError={!!optionValueError}
                                questionType={questionType}
                                onDealbreakerChange={() =>
                                    onDealbreakerAnswerChange?.(optionIndex)
                                }
                                onRemove={() => onRemoveOption?.(optionIndex)}
                            />
                        );
                    }
                )}
                <StyledAddOptionButtonContainer>
                    <PargodaRoundButton
                        variant="outlined"
                        onClick={onAddOption}
                        startIcon={<Icon name="AddRoundedIcon" />}
                        sx={{
                            borderStyle: 'dashed',
                            borderColor: theme.palette.secondary.main,
                            color: theme.palette.secondary.main,
                            height: '60px',
                            fontSize: '14px',
                            textTransform: 'none',
                            borderRadius: 1,
                        }}
                    >
                        {t('saveJob.addOption')}
                    </PargodaRoundButton>
                    {noOptionsErrorMessage && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: theme.palette.error.main,
                            }}
                        >
                            {noOptionsErrorMessage}
                        </Typography>
                    )}
                </StyledAddOptionButtonContainer>
            </StyledOptionsListWrapper>
        </StyledOptionsContainer>
    );
};
